import { FC, SyntheticEvent, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import { ROUTE } from 'common/constants';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';

import history from 'service/customHistory';
import PromptDialog from 'components/common/Dialog/PromptDialog';
import { ButtonVariant } from 'components/common/Button/Button';
import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import ConfigurationFilesList from 'components/configurationFiles/ConfigurationFilesList';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';

import { ADD_CONFIG_FILE, GET_CONFIG_FILES } from 'components/configurationFiles/queries';

import './ConfigurationFilesPage.scss';

type PromptDialog = {
  active: boolean;
  input: string;
};
const promptDialogInitialState: PromptDialog = { active: false, input: '' };

const ConfigurationFilesPage: FC = () => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const showFailureAlert = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.ERROR,
      autoHide: true,
      duration: 1.5,
    });
  };
  // have set fetchPolicy to cache-and-network since there is a caching issues when user is navigating back from file page
  const { data, loading } = useQuery(GET_CONFIG_FILES, { fetchPolicy: 'cache-and-network' });

  const [addConfigFileMutation] = useMutation(ADD_CONFIG_FILE, {
    onError: (error: ApolloError) => {
      showFailureAlert(error.message);
    },
  });
  const [promptDialog, setPromptDialog] = useState<PromptDialog>(promptDialogInitialState);
  function handleConfigCreation(): void {
    setPromptDialog((prevState) => ({ ...prevState, active: true }));
  }
  function handlePromptInput(event: SyntheticEvent): void {
    const input = event.target as HTMLInputElement;
    setPromptDialog((prevState) => ({ ...prevState, input: input.value }));
  }

  function performConfigCreation(): void {
    const nameValue = `${promptDialog.input.charAt(0) === '/' ? '' : '/'}${promptDialog.input}`;
    const names = data?.configFiles.map(({ name }: {name: string}) => name) || [];
    if (names.includes(nameValue)) {
      showFailureAlert(`A file named ${nameValue} already exists`);
    } else {
      addConfigFileMutation({
        variables: {
          name: nameValue,
        },
      }).then(() => {
        history.push({
          pathname: ROUTE.CONFIG_FILE,
          search: `?path=${nameValue}`,
        });
      });
    }
  }

  function closePromptDialog(): void {
    setPromptDialog(promptDialogInitialState);
  }

  const buttons = [
    {
      name: 'Create New File',
      variant: ButtonVariant.PRIMARY,
      onClick: (): void => {
        handleConfigCreation();
      },
    },
  ];

  return (
    <Page docTitle="Configuration Files" pageModifier="Page Page--Standard ConfigurationFilesPage__Container" testId={ELEMENT.configurationFilesPage}>
      <PageHeader title="Configuration Files" helpLink={HELP_ROUTE.HELP_CONFIG} additionalRightButtons={buttons} />
      {loading
        ? <LoadingPlaceholder />
        : <ConfigurationFilesList configurationFiles={data?.configFiles || []} />}
      <PromptDialog
        title="Create New Configuration File"
        label="Please enter the file name or path"
        show={promptDialog.active}
        close={closePromptDialog}
        inline={false}
        value={promptDialog.input}
        onChange={handlePromptInput}
        onSubmit={performConfigCreation}
      />
    </Page>
  );
};

export default ConfigurationFilesPage;
